import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import {Box, List, ListItemButton, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import type {ProtocolRegion} from '../regions';

export type RegionStatus = 'empty' | 'complete' | 'skipped';

interface RegionSidebarProps {
  regions: ProtocolRegion[];
  currentStep: number;
  getRegionStatus: (regionId: string) => RegionStatus;
  onSelect: (step: number) => void;
  t: TFunction;
  variant?: 'permanent' | 'drawer';
}

export default function RegionSidebar({
  regions,
  currentStep,
  getRegionStatus,
  onSelect,
  t,
  variant = 'permanent'
}: RegionSidebarProps) {
  return (
    <Box
      component="aside"
      sx={{
        width: 240,
        flexShrink: 0,
        borderRight: variant === 'drawer' ? 'none' : '1px solid',
        borderColor: 'divider',
        display: variant === 'drawer' ? 'flex' : {xs: 'none', lg: 'flex'},
        flexDirection: 'column',
        bgcolor: 'background.paper'
      }}
    >
      <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
        <Typography variant="overline" color="text.secondary" sx={{fontWeight: 700}}>
          {t('shared/imageProtocol:regions')}
        </Typography>
      </Box>
      <List disablePadding sx={{overflowY: 'auto', flex: 1, py: 1}}>
        {regions.map((region, idx) => {
          const status = getRegionStatus(region.id);
          const active = idx === currentStep;
          return (
            <ListItemButton
              key={region.id}
              selected={active}
              onClick={() => onSelect(idx)}
              sx={{
                mx: 1,
                my: 0.25,
                borderRadius: 1,
                gap: 1.25,
                color: status === 'skipped' ? 'text.disabled' : 'text.primary'
              }}
            >
              <RegionStatusIcon status={status} active={active} />
              <Typography
                variant="body2"
                sx={{
                  textDecoration: status === 'skipped' ? 'line-through' : 'none',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                <Box component="span" sx={{color: 'text.secondary', mr: 0.75}}>
                  {region.number}.
                </Box>
                {region.name}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}

function RegionStatusIcon({status, active}: {status: RegionStatus; active: boolean}) {
  if (status === 'complete') {
    return <CheckCircleIcon fontSize="small" color="success" />;
  }
  if (status === 'skipped') {
    return <SkipNextIcon fontSize="small" sx={{color: 'text.disabled'}} />;
  }
  return (
    <RadioButtonUncheckedIcon
      fontSize="small"
      sx={{color: active ? 'primary.main' : 'text.disabled'}}
    />
  );
}
